import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash, ForkKnife, MagnifyingGlass, Target } from "@phosphor-icons/react";
import BudgetRing from "@/components/BudgetRing";
import PrescribedTargetsModal from "@/components/PrescribedTargetsModal";

/**
 * Renal diet tracker — the legacy `/diet` interactive experience.
 * Renders inside the multi-specialty diet hub when the Nephrology tab is
 * active. Food search + logging + Na/K/P/protein budget rings.
 */
export default function RenalDietTracker() {
  const [query, setQuery] = useState("");
  const [foods, setFoods] = useState([]);
  const [log, setLog] = useState([]);
  const [summary, setSummary] = useState(null);
  const [targetsOpen, setTargetsOpen] = useState(false);

  const load = async () => {
    const [logRes, sumRes] = await Promise.all([api.get("/diet/log"), api.get("/diet/summary")]);
    setLog(logRes.data);
    setSummary(sumRes.data);
  };

  useEffect(() => {
    (async () => {
      const foodsRes = await api.get("/diet/foods");
      setFoods(foodsRes.data);
      await load();
    })();
  }, []);

  const filtered = query
    ? foods.filter(
        (f) =>
          f.name.toLowerCase().includes(query.toLowerCase()) ||
          f.category.toLowerCase().includes(query.toLowerCase())
      )
    : foods.slice(0, 12);

  const addFood = async (name) => {
    await api.post("/diet/log", { food_name: name, servings: 1 });
    toast.success(`${name} added`);
    await load();
  };

  const remove = async (id) => {
    await api.delete(`/diet/log/${id}`);
    await load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="diet-grid">
      <div className="ros-card lg:col-span-3" data-testid="diet-budget-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="ros-label">Today&apos;s renal budget</div>
            <h2 className="font-heading text-2xl mt-1">Nutrient rings</h2>
            <p className="text-xs text-[#A3ACA7] mt-1">
              Uses <span className="text-[#4ADE80]">prescribed targets</span> when set by your clinician; otherwise falls back to KDIGO guidelines.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setTargetsOpen(true)}
            data-testid="open-targets-btn"
            className="rounded-full border-[#272E2B] hover:bg-[#1C201E] gap-2"
          >
            <Target size={16} weight="duotone" /> Prescribed targets
          </Button>
        </div>
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
            <BudgetRing value={summary.totals.sodium} budget={summary.budgets.sodium}
                        color="#FCD34D" label="Sodium" unit="mg" size={160} testId="diet-ring-sodium"
                        source={summary.targets?.sodium?.source} guidelineValue={summary.targets?.sodium?.guideline_value} />
            <BudgetRing value={summary.totals.potassium} budget={summary.budgets.potassium}
                        color="#F472B6" label="Potassium" unit="mg" size={160} testId="diet-ring-potassium"
                        source={summary.targets?.potassium?.source} guidelineValue={summary.targets?.potassium?.guideline_value} />
            <BudgetRing value={summary.totals.phosphorus} budget={summary.budgets.phosphorus}
                        color="#A78BFA" label="Phosphorus" unit="mg" size={160} testId="diet-ring-phosphorus"
                        source={summary.targets?.phosphorus?.source} guidelineValue={summary.targets?.phosphorus?.guideline_value} />
            <BudgetRing value={summary.totals.protein} budget={summary.budgets.protein}
                        color="#818CF8" label="Protein" unit="g" size={160} testId="diet-ring-protein"
                        source={summary.targets?.protein?.source} guidelineValue={summary.targets?.protein?.guideline_value} />
          </div>
        )}
      </div>

      <div className="ros-card lg:col-span-2" data-testid="food-picker">
        <div className="flex items-center gap-3">
          <MagnifyingGlass size={20} className="text-[#A3ACA7]" />
          <Input
            data-testid="food-search-input"
            placeholder="Search foods (banana, salmon, spinach…)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="bg-transparent border-none focus-visible:ring-0 text-base placeholder:text-[#A3ACA7]"
          />
        </div>
        <div className="ros-divider mt-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 max-h-[520px] overflow-y-auto pr-1">
          {filtered.map((f) => (
            <button
              key={f.name}
              onClick={() => addFood(f.name)}
              data-testid={`food-item-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="ros-card-raised text-left hover:border-[#4ADE80]/30 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="font-medium">{f.name}</div>
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#272E2B] text-[#A3ACA7]">
                  {f.category}
                </span>
              </div>
              <div className="text-xs text-[#A3ACA7] mt-1">{f.serving}</div>
              <div className="grid grid-cols-4 gap-2 mt-3 text-[10px] text-[#A3ACA7]">
                <div><div className="text-white font-semibold">{Math.round(f.sodium * f.unit_g / 100)}</div>Na mg</div>
                <div><div className="text-white font-semibold">{Math.round(f.potassium * f.unit_g / 100)}</div>K mg</div>
                <div><div className="text-white font-semibold">{Math.round(f.phosphorus * f.unit_g / 100)}</div>P mg</div>
                <div><div className="text-white font-semibold">{(f.protein * f.unit_g / 100).toFixed(1)}</div>Prot g</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="ros-card" data-testid="diet-log-card">
        <div className="flex items-center gap-2">
          <ForkKnife size={18} className="text-[#4ADE80]" weight="duotone" />
          <div className="ros-label">Today&apos;s log</div>
        </div>
        <div className="text-xs text-[#A3ACA7] mt-1">{log.length} entries</div>
        <div className="mt-4 space-y-2 max-h-[520px] overflow-y-auto pr-1">
          {log.length === 0 && (
            <div className="text-sm text-[#A3ACA7] py-6 text-center">
              No entries yet. Add a food to see your budget move.
            </div>
          )}
          {log.map((e) => (
            <div key={e.id} className="ros-card-raised flex items-center justify-between"
                 data-testid={`log-entry-${e.id}`}>
              <div>
                <div className="font-medium text-sm">{e.food_name}</div>
                <div className="text-[10px] text-[#A3ACA7] mt-0.5">
                  Na {Math.round(e.sodium)} · K {Math.round(e.potassium)} · P {Math.round(e.phosphorus)} · Prot {e.protein.toFixed(1)}g
                </div>
              </div>
              <button onClick={() => remove(e.id)} className="text-[#A3ACA7] hover:text-[#F87171] p-1"
                      data-testid={`delete-log-${e.id}`}>
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <PrescribedTargetsModal
        open={targetsOpen}
        onOpenChange={setTargetsOpen}
        onSaved={load}
      />
    </div>
  );
}
